import { Directive, Input, OnInit, Type, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormComponent, FormService } from '@woolworthsnz/form';
import { GridContainerComponent as RecipeGridComponent } from '@woolworthsnz/recipe';
import { SearchPageSizeLimits } from '@woolworthsnz/search';
import { AccordionComponent, AlertComponent, ContainerDisplayType, ShopperService } from '@woolworthsnz/styleguide';
import {
	ArticleComponent,
	FormCheckboxComponent,
	FormDropdownComponent,
	FormEmailComponent,
	FormHiddenComponent,
	FormMultilineComponent,
	FormNumberComponent,
	FormSingleLineComponent,
	FormSubmitComponent,
	HtmlComponent,
	ImageComponent,
	ImageNavigationComponent,
	ProductGridComponent,
} from '../components';
import { ServiceBarComponent } from '../components/service-bar/service-bar.component';
import { TextComponent } from '../components/text/text.component';
import { EDRBoostOffersCarouselComponent, EDRNotificationPanelComponent } from '@woolworthsnz/everyday-rewards';
import { Observable, map, take } from 'rxjs';

@Directive({
	selector: '[dcDynamicComponent],[dynamicComponent]',
	providers: [FormService],
	standalone: true,
})
export class DynamicComponentDirective implements OnInit {
	@Input('dynamicComponent') componentName: string;
	// eslint-disable-next-line @angular-eslint/no-input-rename
	@Input('data') inputData: any;

	@Input() formGroup: FormGroup;

	@Input() dynamicForm: FormComponent;

	@Input() carouselName?: string | null;
	@Input() isCarouselItem = false;
	@Input() position = 0;

	private componentBuilders: { [key: string]: () => void } = {
		accordion: this.buildAccordion,
		html: this.buildHtml,
		image: this.buildImage,
		productgrid: this.buildProductGrid,
		servicebar: this.buildServiceBar,
		imagenavigation: this.buildImageNavigation,
		recipegrid: this.buildRecipeGrid,
		submit: this.buildSubmit,
		dropdown: this.buildDropdown,
		checkbox: this.buildCheckbox,
		email: this.buildEmail,
		singlelinetext: this.buildSingleLineText,
		hidden: this.buildHidden,
		multilinetext: this.buildMultilineText,
		number: this.buildNumber,
		article: this.buildArticle,
		text: this.buildText,
		alertbanner: this.buildAlertBanner,
		edroffersnotificat: this.buildEdrOffersNotification, // NOT a typo. There is a character limit in place
		edrboostofferscrsl: this.buildEdrBoostOffersCarousel,
	};

	private isAuthenticated$: Observable<boolean>;

	constructor(
		private vc: ViewContainerRef,
		private formService: FormService,
		private shopperService: ShopperService
	) {}

	ngOnInit(): void {
		this.isAuthenticated$ = this.shopperService.state$.pipe(map((shopper) => !!shopper.isLoggedIn));

		const componentName = (this.componentName || '').toLowerCase();
		const builderMethod = this.componentBuilders[componentName];
		if (builderMethod) {
			builderMethod.bind(this)();
		}
	}

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	getComponentRef<T>(component: Type<T>) {
		this.vc.clear();
		return this.vc.createComponent(component);
	}

	private buildAccordion(): void {
		const componentRef = this.getComponentRef(AccordionComponent);
		const { serializedProperties } = this.inputData;
		componentRef.instance.items = JSON.parse(serializedProperties.accordionitems);
		componentRef.instance.analyticsID = this.inputData.name;
	}

	private buildHtml(): void {
		const componentRef = this.getComponentRef(HtmlComponent);
		const { text } = this.inputData;
		componentRef.instance.content = text;
	}

	private buildImage(): void {
		const componentRef = this.getComponentRef(ImageComponent);
		const { name, text, imagePath, link, linkTarget, serializedProperties, altText } = this.inputData;

		componentRef.instance.image = {
			name,
			text,
			imagePath,
			link,
			linkTarget,
			serializedProperties,
			altText,
		};
		componentRef.instance.carouselName = this.carouselName;
		componentRef.instance.isCarouselItem = this.isCarouselItem;
		componentRef.instance.ariaLabelId = this.inputData.id;
		componentRef.instance.position = this.position;
	}

	private buildProductGrid(): void {
		const componentRef = this.getComponentRef(ProductGridComponent);
		const { serializedProperties, imagePath, name } = this.inputData;

		const instanceName = this.carouselName || name;

		componentRef.instance.name = instanceName;
		componentRef.instance.title = serializedProperties.title;
		componentRef.instance.filteringEnabled = serializedProperties.filteringEnabled ?? false;
		componentRef.instance.filterPageSize = serializedProperties.filterPageSize ?? SearchPageSizeLimits.Maximum;
		componentRef.instance.filterDynamicTitleEnabled = serializedProperties.filterDynamicTitleEnabled ?? false;
		componentRef.instance.productGroupId = serializedProperties.productgroupid;
		componentRef.instance.titleIsMobileOnly = serializedProperties.titleIsMobileOnly;
		componentRef.instance.displayType = serializedProperties.displaytype;
		componentRef.instance.ctaEnabled = serializedProperties.cta;
		componentRef.instance.ctaAsHeader = serializedProperties.hero;
		componentRef.instance.ctaUrl = serializedProperties.ctaUrl;
		componentRef.instance.ctaLinkText = serializedProperties.linkText;
		componentRef.instance.ctaImagePath = imagePath;
		componentRef.instance.ctaImageAltText = serializedProperties.altText;
		componentRef.instance.productGroupSort = serializedProperties.productgroupsort;
		componentRef.instance.maxProductsCount = serializedProperties.maxproductscount;
		componentRef.instance.dataSourceEnabled = serializedProperties.dataSourceEnabled;
		componentRef.instance.selectedDataSource = serializedProperties.selectedDataSource;
		componentRef.instance.analyticsID = instanceName;
	}

	private buildServiceBar(): void {
		const componentRef = this.getComponentRef(ServiceBarComponent);
		const { serializedProperties } = this.inputData;
		componentRef.instance.icons = serializedProperties.slice(0, 6);
	}

	private buildImageNavigation(): void {
		const { text, link, linkTarget, imagePath, altText } = this.inputData;

		const componentRef = this.getComponentRef(ImageNavigationComponent);
		const component = componentRef.instance;

		component.text = text;
		component.link = link;
		component.linkTarget = linkTarget;
		component.image = imagePath;
		component.imageAlt = altText;
	}

	private buildRecipeGrid(): void {
		const componentRef = this.getComponentRef(RecipeGridComponent);
		const component = componentRef.instance;

		const { serializedProperties } = this.inputData;

		component.categoryId = serializedProperties.categoryId;
		component.mode = serializedProperties.mode;
		component.page = serializedProperties.page;
		component.pageSize = serializedProperties.pageSize;
		component.recipeIds = serializedProperties.recipeIds;
		component.search = serializedProperties.search;
		component.sort = serializedProperties.sort;
		component.target = serializedProperties.target;
		component.trackPromotionEvents = true;

		// ngOnChanges won't run when updating inputs through the instance reference
		component.ngOnChanges();
	}

	private buildSubmit(): void {
		const { serializedProperties } = this.inputData;
		const componentRef = this.getComponentRef(FormSubmitComponent);
		const component = componentRef.instance;
		component.label = serializedProperties.label;
		if (this.formGroup) {
			component.group = this.formGroup;
		}
	}

	private buildDropdown(): void {
		const { serializedProperties, id } = this.inputData;
		const controlName = id.toString();
		const componentRef = this.getComponentRef(FormDropdownComponent);
		const component = componentRef.instance;
		component.label = serializedProperties.label;
		component.options = serializedProperties.options;
		const validators = [];
		if (serializedProperties.required) {
			validators.push(Validators.required);
		}
		if (this.formGroup) {
			this.formService.addControlToParent(controlName, validators, this.formGroup);
			component.group = this.formGroup;
			component.formControlName = controlName;
		}
	}

	private buildCheckbox(): void {
		const { serializedProperties, id, type } = this.inputData;
		const controlName = id.toString();
		const componentRef = this.getComponentRef(FormCheckboxComponent);
		const component = componentRef.instance;

		// If we don't get a label, use the type instead and hide the legend
		component.label = !!serializedProperties.label ? serializedProperties.label : type;
		component.hideLegend = !serializedProperties.label;

		component.options = serializedProperties.options;
		component.layout = serializedProperties.optionsLayout;
		if (this.formGroup) {
			const controls: { [key: string]: FormControl } = {};
			component.options.forEach((option: { text: string; value: string }) => {
				controls[`${controlName}_${option.value}`] = new FormControl(false);
			});
			const control = new FormGroup(controls);
			this.formGroup.addControl(controlName, control);
			component.group = control;
			component.controlName = controlName;
		}
	}

	private buildEmail(): void {
		const { serializedProperties, id } = this.inputData;
		const controlName = id.toString();
		const componentRef = this.getComponentRef(FormEmailComponent);
		const component = componentRef.instance;
		component.label = serializedProperties.label;
		component.placeholder = serializedProperties.placeholder;
		const validators = [];
		if (serializedProperties.required) {
			validators.push(Validators.required);
		}
		if (this.formGroup) {
			this.formService.addControlToParent(controlName, validators, this.formGroup);
			component.group = this.formGroup;
			component.formControlName = controlName;
		}
	}

	private buildSingleLineText(): void {
		const { serializedProperties, id } = this.inputData;
		const controlName = id.toString();
		const componentRef = this.getComponentRef(FormSingleLineComponent);
		const component = componentRef.instance;
		component.label = serializedProperties.label;
		component.placeholder = serializedProperties.placeholder;
		const validators = [];
		if (serializedProperties.required) {
			validators.push(Validators.required);
		}
		if (serializedProperties.maxLength) {
			validators.push(Validators.maxLength(serializedProperties.maxLength));
		}
		if (this.formGroup) {
			this.formService.addControlToParent(controlName, validators, this.formGroup);
			component.group = this.formGroup;
			component.formControlName = controlName;
		}
	}

	private buildHidden(): void {
		const { serializedProperties, id } = this.inputData;
		const controlName = id.toString();
		const componentRef = this.getComponentRef(FormHiddenComponent);
		const component = componentRef.instance;
		component.name = serializedProperties.label;
		component.value = serializedProperties.value;
		if (this.formGroup) {
			this.formService.addControlToParent(controlName, [], this.formGroup);
			component.group = this.formGroup;
			component.formControlName = controlName;
		}
	}

	private buildMultilineText(): void {
		const { serializedProperties, id } = this.inputData;
		const controlName = id.toString();
		const componentRef = this.getComponentRef(FormMultilineComponent);
		const component = componentRef.instance;
		component.label = serializedProperties.label;
		component.placeholder = serializedProperties.placeholder;
		const validators = [];
		if (serializedProperties.required) {
			validators.push(Validators.required);
		}
		if (serializedProperties.maxLength) {
			validators.push(Validators.maxLength(serializedProperties.maxLength));
		}
		component.rows = serializedProperties.rows;
		component.columns = serializedProperties.columns;
		if (this.formGroup) {
			this.formService.addControlToParent(controlName, validators, this.formGroup);
			component.group = this.formGroup;
			component.formControlName = controlName;
		}
	}

	private buildNumber(): void {
		const { serializedProperties, id } = this.inputData;
		const controlName = id.toString();
		const componentRef = this.getComponentRef(FormNumberComponent);
		const component = componentRef.instance;
		component.label = serializedProperties.label;
		const validators = [];
		if (serializedProperties.required) {
			validators.push(Validators.required);
		}
		if (serializedProperties.min) {
			validators.push(Validators.min(serializedProperties.min));
			component.min = serializedProperties.min;
		}
		if (serializedProperties.max) {
			validators.push(Validators.max(serializedProperties.max));
			component.max = serializedProperties.max;
		}
		if (this.formGroup) {
			this.formService.addControlToParent(controlName, validators, this.formGroup, [], null);
			component.group = this.formGroup;
			component.formControlName = controlName;
		}
	}

	private buildArticle(): void {
		const componentRef = this.getComponentRef(ArticleComponent);
		const component = componentRef.instance;

		const { serializedProperties } = this.inputData;

		component.id = this.inputData.id;
		component.name = this.inputData.name;
		component.link = serializedProperties.link;
		component.target = serializedProperties.target;
		component.image = serializedProperties.image;
		component.imageAlt = serializedProperties.imageAlt;
		component.type = serializedProperties.type;
		component.title = serializedProperties.title;
		component.text = serializedProperties.text;
		component.carouselName = this.carouselName;
		component.position = this.position + 1;
	}

	private buildText(): void {
		const componentRef = this.getComponentRef(TextComponent);
		const { text } = this.inputData;
		componentRef.instance.text = text;
	}

	private buildAlertBanner(): void {
		const componentRef = this.getComponentRef(AlertComponent);
		const { serializedProperties } = this.inputData;
		componentRef.instance.showTitle = serializedProperties.showTitle;
		componentRef.instance.title = serializedProperties.title;
		componentRef.instance.description = serializedProperties.description;
		componentRef.instance.showIcon = serializedProperties.showIcon ?? true;
		componentRef.instance.icon = serializedProperties.iconName ?? 'alert';
		componentRef.instance.iconsize = serializedProperties.iconSize ?? 'default';
		componentRef.instance.candismiss = serializedProperties.canDismiss;
		componentRef.instance.type = serializedProperties.alertType ?? 'info';
		componentRef.instance.animate = serializedProperties.animate ?? true;
	}

	private buildEdrOffersNotification(): void {
		const componentRef = this.getComponentRef(EDRNotificationPanelComponent);
		const { serializedProperties } = this.inputData;

		componentRef.instance.image = {
			url: serializedProperties.imagePath,
			alt: serializedProperties.imageAltText,
		};
		componentRef.instance.imageType = serializedProperties.imageType;
		componentRef.instance.title = serializedProperties.title;
		componentRef.instance.noOffersTitle = serializedProperties.noOffersTitle;
		componentRef.instance.description = serializedProperties.description;
		componentRef.instance.noOffersDescription = serializedProperties.noOffersDescription;
		componentRef.instance.ctaText = serializedProperties.ctaText;
		componentRef.instance.noOffersCtaText = serializedProperties.noOffersCtaText;
		componentRef.instance.ctaLink = serializedProperties.ctaLink;
		componentRef.instance.noOffersCtaLink = serializedProperties.noOffersCtaLink;
	}

	private buildEdrBoostOffersCarousel(): void {
		this.isAuthenticated$.pipe(take(1)).subscribe((isAuthenticated) => {
			if (isAuthenticated) {
				const componentRef = this.getComponentRef(EDRBoostOffersCarouselComponent);
				const { serializedProperties } = this.inputData;

				componentRef.instance.name = serializedProperties.name;
				componentRef.instance.enableArrowNavigation = serializedProperties.enableArrowNavigation ?? false;
				componentRef.instance.displayType = serializedProperties.displayType ?? ContainerDisplayType.dynamic;
				componentRef.instance.align = serializedProperties.align;
				componentRef.instance.columnsConfig = serializedProperties.columnsConfig;
			}
		});
	}
}
